import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from './components/Layout';
import AppIcon from './components/AppIcon';
import { ArrowRight, Apple, Mail, Search, Sparkle } from './components/Icons';
import { CONTACT_EMAIL, LISTED_APPS } from './data/apps';
import './App.css';

function AppCard({ app, index }) {
    // Feed the cursor position to CSS so the card's spotlight follows the pointer.
    const onPointerMove = useCallback(event => {
        const rect = event.currentTarget.getBoundingClientRect();
        event.currentTarget.style.setProperty('--spot-x', `${event.clientX - rect.left}px`);
        event.currentTarget.style.setProperty('--spot-y', `${event.clientY - rect.top}px`);
    }, []);

    const live = app.status === 'live';

    return (
        <article
            className={`card card-glow app-card${live ? '' : ' is-soon'}`}
            style={{ animationDelay: `${Math.min(index, 8) * 55}ms` }}
            onPointerMove={onPointerMove}
        >
            <div className="app-card-top">
                <AppIcon app={app} />
                <div className="app-card-title">
                    <h3>
                        <Link to={`/support/${app.slug}`} className="stretch">
                            {app.name}
                        </Link>
                    </h3>
                    <p>{app.tagline}</p>
                </div>
            </div>

            <div className="app-card-foot">
                <span className={`pill ${live ? 'pill-live' : 'pill-soon'}`}>
                    {live ? 'Available' : 'Coming soon'}
                </span>
                <span className="app-card-cta">
                    Support
                    <ArrowRight />
                </span>
            </div>
        </article>
    );
}

export default function App() {
    const [query, setQuery] = useState('');

    useEffect(() => {
        document.title = 'App Support | Aurescope Interactive';
    }, []);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return LISTED_APPS;
        return LISTED_APPS.filter(app =>
            `${app.name} ${app.tagline} ${app.platform}`.toLowerCase().includes(q)
        );
    }, [query]);

    const liveCount = LISTED_APPS.filter(app => app.status === 'live').length;

    return (
        <Layout>
            <section className="hero">
                <span className="eyebrow">
                    <span className="dot" />
                    {liveCount} apps shipping on the App Store
                </span>

                <h1>
                    Help for every app
                    <br />
                    <span className="grad-text">we’ve ever shipped.</span>
                </h1>

                <p className="lede">
                    Pick an app to see what it does, read its privacy policy, or get in touch.
                    Questions, bugs and suggestions are all welcome.
                </p>

                <div className="hero-actions">
                    <a className="btn btn-primary" href={`mailto:${CONTACT_EMAIL}`}>
                        <Mail />
                        Contact support
                    </a>
                    <a className="btn" href="#apps">
                        <Sparkle />
                        Browse apps
                    </a>
                </div>
            </section>

            <section id="apps" aria-labelledby="apps-heading">
                <div className="section-head">
                    <h2 id="apps-heading">All apps</h2>
                    <span className="pill pill-neutral">
                        <Apple width="12" height="12" />
                        iOS &amp; macOS
                    </span>
                </div>

                <div className="toolbar">
                    <div className="search">
                        <Search />
                        <input
                            type="search"
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                            placeholder="Search apps…"
                            aria-label="Search apps"
                        />
                    </div>
                    <span className="count">
                        {results.length} of {LISTED_APPS.length}
                    </span>
                </div>

                {results.length > 0 ? (
                    <div className="grid">
                        {results.map((app, index) => (
                            <AppCard key={app.slug} app={app} index={index} />
                        ))}
                    </div>
                ) : (
                    <div className="card contact">
                        <div className="contact-text">
                            <strong>No apps match “{query}”</strong>
                            <p>Try a different name, or email us and we’ll point you the right way.</p>
                        </div>
                        <a className="btn" href={`mailto:${CONTACT_EMAIL}`}>
                            <Mail />
                            Email support
                        </a>
                    </div>
                )}
            </section>

            <section className="card card-glow help-band">
                <div className="help-band-text">
                    <h2>Still stuck?</h2>
                    <p>
                        Tell us the app, your device and what you were doing when it went wrong. We read
                        every message.
                    </p>
                </div>
                <a className="btn btn-primary" href={`mailto:${CONTACT_EMAIL}`}>
                    <Mail />
                    {CONTACT_EMAIL}
                </a>
            </section>
        </Layout>
    );
}
